import {
  Checkbox,
  CircularProgress,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useSWR from 'swr';

import RESTAPIError from '../../interfaces/RESTAPIError';
import LimitViewReportProps from '../../interfaces/userRoleSettingsProps';
import customerAuthService from '../../services/customerAuthService';
import { CustomerListItem } from '../partner/filter/companyFilterButton';

export interface CustomerListItemPatch extends CustomerListItem {
  has_access: boolean;
}

interface CustomerAccessComponentProps extends LimitViewReportProps {
  selectedCustomerList: CustomerListItemPatch[];
  error: boolean;
  loading: boolean;
  mutate: () => void;
}

export default function CustomerAccessComponent({
  userId,
  selectedCustomerList,
  loading,
  mutate,
}: CustomerAccessComponentProps) {
  const [allCustomer, setAllCustomer] = useState<CustomerListItem[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<
  CustomerListItemPatch[]
  >([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { data: tenantList, isLoading } = useSWR('tenant');

  useEffect(() => {
    const list = tenantList?.assigned_tenants;
    if (list && Array.isArray(list)) setAllCustomer(list);
  }, [tenantList]);

  useEffect(() => {
    setSelectedCustomers(selectedCustomerList);
  }, [selectedCustomerList]);

  const updateComponentStateViaAPI = async (
    selectedCostomers: CustomerListItemPatch[],
  ) => {
    try {
      await customerAuthService.updateCustomerAccess(
        userId,
        localStorage.getItem('slug'),
        selectedCostomers,
      );
      mutate();
    } catch (error) {
      const apiError = error as RESTAPIError;
      setErrorMessage(apiError.message);
    }
  };

  const handleCheckboxChange = (selectedCustomer: CustomerListItem) => () => {
    setSelectedCustomers((prevSelected) => {
      const existingIndex = prevSelected.findIndex(
        (customer) => customer.name === selectedCustomer.name,
      );
      if (existingIndex !== -1) {
        // Remove item if already exists
        const updatedSelection = [...prevSelected];
        const checked = updatedSelection[existingIndex].has_access;
        updatedSelection[existingIndex].has_access = !checked;
        return updatedSelection;
      } else {
        // Add the item if it doesn't exist
        return [...prevSelected, { has_access: true, ...selectedCustomer }];
      }
    });
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    updateComponentStateViaAPI(selectedCustomers);
  };

  return (
    <>
      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk',
          fontSize: '16px',
          fontWeight: 500,
          textAlign: 'left',
          mb: '12px',
          mt: '24px',
        }}
      >
        <FormattedMessage defaultMessage='Customer Access' />
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk',
          color: 'grey',
          fontSize: '14px',
          fontWeight: 400,
          mb: '16px',
        }}
      >
        <FormattedMessage defaultMessage='Assign which customer this user can manage' />
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: 'auto',
          alignItems: 'center',
          Height: '34px',
        }}
      >
        <FormControl
          sx={{
            minWidth: '93px',
            height: '34px',
          }}
        >
          <Select
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            sx={{
              height: '100%',
            }}
            multiple
            displayEmpty
            value={selectedCustomers}
            renderValue={(selected) => {
              if (selected.length == 0) {
                return (
                  <Box
                    sx={{
                      mt: '6px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '14px', color: 'rgba(135, 135, 135, 1)' }}
                    >
                      <FormattedMessage defaultMessage='Customer' />
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box
                  sx={{
                    mt: '6px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '14px', color: 'rgba(135, 135, 135, 1)' }}
                  >
                    <FormattedMessage defaultMessage='Customer' />
                  </Typography>
                  <Box
                    sx={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: 'rgba(0, 206, 207, 1)',
                      borderRadius: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center', // Horizontally center the content
                      ml: '4px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', fontWeight: 700, color: 'white' }}
                    >
                      {
                        selected?.filter((customer) => customer.has_access)
                          ?.length
                      }
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            inputProps={{ 'aria-label': 'Without label' }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: {
                style: {
                  marginTop: '4px',
                  maxHeight: '226px', // Set the maximum height for the dropdown menu
                },
              },
            }}
          >
            {allCustomer.map((customer) => (
              <MenuItem
                key={customer.id}
                value={customer.name}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(240, 240, 240, 1)', // Change background color on hover
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'lightgreen', // Change background color when selected
                  },
                }}
              >
                <Checkbox
                  // checked={selectedReports.indexOf(name) !== -1}
                  // onChange={handleCheckboxChange(name)}
                  checked={selectedCustomers.some(
                    (customers) =>
                      customers.name === customer.name && customers.has_access,
                  )}
                  onChange={handleCheckboxChange(customer)}
                  sx={{
                    width: '14px',
                    height: '14px',
                    mr: '8px',
                    borderRadius: '1.5px',
                    '&.Mui-checked': {
                      color: 'rgba(0, 206, 207, 1)', // Change checked checkbox color
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '14px',
                    },
                  }}
                />
                <ListItemText
                  onClick={handleCheckboxChange(customer)}
                  primary={customer.name}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dyanmically generate the boxes with the names of the selected reports that are in the SelectedReports Array */}
        {isLoading || loading ? (
          <CircularProgress
            size={20}
            style={{ marginLeft: '19px', color: 'black' }}
          />
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              {selectedCustomers
                ?.filter((customer) => customer.has_access)
                ?.map((customer, index) => (
                  <Box
                    key={index}
                    sx={{
                      ml: '12px',
                      mt: '10px',
                      width: 'auto',
                      height: '34px',
                      border: '1px solid rgba(135, 135, 135, 1)',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingY: '8px',
                      paddingX: '12px',
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', textAlign: 'center' }}>
                      {customer.name}
                    </Typography>
                  </Box>
                ))}
            </Box>
            {errorMessage ? (
              <Typography sx={{ color: 'red' }}>{errorMessage}</Typography>
            ) : (
              ''
            )}
          </>
        )}
      </Box>
    </>
  );
}
